/**
 * @file ColorRoleRow.tsx
 * @description Строка роли цвета на вкладке «Цвета» (phase19.16 Р1;
 * uiux-design-r1 §1): образец, название, «где используется», HEX, метка
 * контраста и при «!»/«✕» — пояснение с «Взять ближайший #… · 5,2».
 * Строка целиком открывает окно цвета (высота 56, образец 32).
 */

import { SITE_COLOR_ROLE_INFO, type SiteColorRole, type SiteColors } from "@chaiberi/shared";
import { ChevronRight } from "lucide-react";

import { Button } from "../../ui/button";
import { ContrastBadge } from "./ContrastBadge";
import { buttonTextView, CONTRAST_LEVEL_HINT, roleContrast } from "../../../lib/site-view/color-roles";

interface Props {
  role: SiteColorRole;
  colors: SiteColors;
  onOpen: () => void;
  onApply: (hex: string) => void;
}

/** Образец цвета: квадрат 32 с тонкой рамкой (белое на белом тоже видно). */
export function Swatch({ color, size = 32 }: { color: string; size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-block shrink-0 rounded-md [box-shadow:inset_0_0_0_1px_var(--border)]"
      style={{ background: color, width: size, height: size }}
    />
  );
}

export function ColorRoleRow({ role, colors, onOpen, onApply }: Props) {
  const info = SITE_COLOR_ROLE_INFO[role];
  const contrast = roleContrast(colors, role);
  const button = role === "button" ? buttonTextView(colors) : null;
  const hex = colors[role].toUpperCase();

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${info.label}: ${hex}. Изменить цвет`}
        className="flex min-h-14 w-full items-center gap-3 rounded-lg px-2 py-2 text-left outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Swatch color={colors[role]} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="type-body font-medium text-on-surface">{info.label}</span>
          <span className="type-caption truncate text-on-surface-variant">
            <span className="font-mono">{hex}</span> · {info.usedFor}
          </span>
          {button ? (
            <span className="type-caption text-on-surface-variant">
              текст на кнопке: {button.toneLabel} · {button.ratioLabel} {button.mark}
            </span>
          ) : null}
        </span>
        {contrast ? <ContrastBadge level={contrast.level} ratioLabel={contrast.ratioLabel} mark={contrast.mark} /> : null}
        <ChevronRight aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
      </button>
      {contrast && contrast.level !== "ok" ? (
        <div className="flex flex-col gap-1 pb-2 pl-2 pr-2 sm:pl-12">
          <p className="type-caption text-on-surface-variant">
            {role === "button"
              ? `Кнопка сливается с фоном страницы (норма ${contrast.minLabel}).`
              : `Мелкий текст этим цветом ${CONTRAST_LEVEL_HINT[contrast.level]} (норма ${contrast.minLabel}).`}
          </p>
          {contrast.nearest ? (
            <Button variant="outline" className="h-auto max-w-full self-start whitespace-normal py-2 text-left" onClick={() => onApply(contrast.nearest!.hex)}>
              <Swatch color={colors[role]} size={16} />→<Swatch color={contrast.nearest.hex} size={16} />
              Взять ближайший {contrast.nearest.hex.toUpperCase()} · {contrast.nearest.ratioLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
